import {
  Component,
  viewChild,
  ChangeDetectionStrategy,
  signal,
} from '@angular/core';
import { Column } from 'src/app/model/common/column';
import { BaseTableService } from 'src/app/services/table/base-table.service';
import { isNotNullOrUndefined, isNullOrUndefined } from 'src/app/utils/utils';
import { GeneralGridComponent } from '../../components/general-grid.component';
import { BaseContainer } from '../base-container';
import { GridLazyLoadEvent } from '../../components/base-grid.component';
import { FilterMetadata } from '@openng/optimus-ui/api';

export type PageRequest = {
  page: number;
  pageSize: number;
};

export type LazyLoadFilters = {
  [key: string]: FilterMetadata | FilterMetadata[] | undefined;
};

@Component({
  selector: 'app-search-container',
  templateUrl: './search-container.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./search-container.component.scss'],
})
export class SearchContainerComponent<
  T extends { id: string },
> extends BaseContainer {
  readonly grid = viewChild<GeneralGridComponent>('grid');

  // Signal-backed so OnPush subclasses re-render when async queries assign them.
  // ES-private (#) so they can't collide with subclass member names.
  readonly #columns = signal<Column[]>([]);
  readonly #elements = signal<T[]>([]);
  readonly #totalRecords = signal(-1);
  readonly #rows = signal(50);
  readonly #loading = signal(true);

  get columns(): Column[] {
    return this.#columns();
  }
  set columns(value: Column[]) {
    this.#columns.set(value);
  }

  get elements(): T[] {
    return this.#elements();
  }
  set elements(value: T[]) {
    this.#elements.set(value);
  }

  get totalRecords(): number {
    return this.#totalRecords();
  }
  set totalRecords(value: number) {
    this.#totalRecords.set(value);
  }

  get rows(): number {
    return this.#rows();
  }
  set rows(value: number) {
    this.#rows.set(value);
  }

  get loading(): boolean {
    return this.#loading();
  }
  set loading(value: boolean) {
    this.#loading.set(value);
  }

  navigatedBackTo = false;

  notFromSort = true;
  protected tableService!: BaseTableService<T>;

  constructor() {
    super();
  }

  protected setTableService(svc: BaseTableService<T>) {
    this.tableService = svc;
  }

  protected applyPreviousFilters() {
    const lazyEvent = this.tableService.getLastLazyLoad()
      ? this.tableService.getLastLazyLoad()
      : null;

    if (lazyEvent && lazyEvent.filters) {
      Object.keys(lazyEvent.filters).forEach((key) => {
        const idx = this.columns.findIndex((l: Column) => l.field === key);
        const filters = lazyEvent.filters;
        if (
          isNotNullOrUndefined(idx) &&
          idx >= 0 &&
          isNotNullOrUndefined(filters) &&
          isNotNullOrUndefined(this.columns[idx]) &&
          isNotNullOrUndefined(filters[key])
        ) {
          this.columns[idx].value = (filters[key] as FilterMetadata).value;
          const grid = this.grid();
          const table = grid?.table();
          if (grid && table) {
            table.filters[key] = filters[key];
          }
        }
      });
    }
  }

  protected applyPreviousData() {
    this.notFromSort = false;
    const lazy = this.tableService.getLastLazyLoad();

    const grid = this.grid();
    if (lazy && !isNullOrUndefined(grid)) {
      const table = grid?.table();
      if (table) {
        table.sortField = lazy.sortField as string | null | undefined;
        table.sortOrder = lazy.sortOrder!;
      }
    }
    this.notFromSort = true;
    this.lazyLoad({ lazy: this.tableService.getLastLazyLoad()! });
  }

  lazyLoad($event: GridLazyLoadEvent, setLoading = true) {
    if (!this.navigatedBackTo) {
      if (!$event && this.tableService.getLastLazyLoad()) {
        $event = { lazy: this.tableService.getLastLazyLoad()! };
      } else if (!$event) {
        $event = { lazy: {} };
      }

      if (setLoading) {
        this.loading = true;
      }
      const req = {
        page: 0,
        pageSize: 50,
      };

      const grid = this.grid();
      const lazy = $event?.lazy;
      if (lazy && grid) {
        req.page = lazy.first! / lazy.rows!;
        req.pageSize = lazy.rows!;
        this.rows = lazy.rows!;
        const table = grid.table();
        if (table) {
          table.first = lazy.first;
        }
        if (this.notFromSort) {
          this.tableService.setLastLazyLoad(lazy);
        }
      }
      const filters =
        $event && $event.lazy && $event.lazy.filters ? $event.lazy.filters : {};
      for (const filter in filters) {
        const val = filters[filter];
        if (Array.isArray(val) && val.length === 1) {
          filters[filter] = val[0];
        }
      }
      this.handleLazyLoad(req, $event, filters);
    } else {
      this.navigatedBackTo = false;
    }
  }

  /** Method needs to be implemented in subclasses */
  handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    throw new Error('Method not implemented');
  }

  finalizeQuery(totalRecords: number) {
    this.totalRecords = totalRecords;
    this.tableService.setLastTotalRecords(this.totalRecords);
    this.loading = false;
  }

  isMobileDevice(): boolean {
    return this._deviceService.isMobile();
  }

  buildNetworkSortQuery(
    $event: any,
    defaultSort: string,
    defaultDir = 'ASC'
  ): any {
    const sort: any = {};
    if ($event.lazy && $event.lazy.sortField) {
      sort[$event.lazy.sortField] =
        $event.lazy.sortOrder && $event.lazy.sortOrder === 1 ? 'ASC' : 'DESC';
      this.tableService.setLastSortField($event.lazy.sortField);
      this.tableService.setLastSortDirection($event.lazy.sortOrder);
    } else {
      sort[defaultSort] = defaultDir;
      this.tableService.setLastSortField(defaultSort);
      this.tableService.setLastSortDirection(1);
    }
    return sort;
  }

  clearScreen() {
    this.grid()?.resetTable();
    this.elements = [];
  }
}
